import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Modal,
  message,
  Statistic,
  Row,
  Col,
  Tag,
  Descriptions,
  Form,
  Badge,
  Popconfirm,
  Tabs,
  Typography,
  Alert,
  Progress
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  WifiOutlined,
  DisconnectOutlined,
  ExclamationCircleOutlined,
  ClearOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import API from '../services/api';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;
const { confirm } = Modal;

const SessionManagement = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [kickoutLogs, setKickoutLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [logPagination, setLogPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState(undefined);
  const [isActiveFilter, setIsActiveFilter] = useState(undefined);
  const [kickTypeFilter, setKickTypeFilter] = useState(undefined);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetailVisible, setSessionDetailVisible] = useState(false);
  const [kickFormVisible, setKickFormVisible] = useState(false);
  const [kickForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('sessions');

  // 加载会话统计
  const loadStats = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/sessions/stats');
      setStats(response.data);
    } catch (error) {
      console.error('加载统计失败:', error);
    }
  }, []);

  // 加载会话列表
  const loadSessions = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: searchText,
        accountType: accountTypeFilter,
        isActive: isActiveFilter
      };

      const response = await API.get('/api/admin/sessions', { params });
      setSessions(response.data.sessions);
      setPagination({
        current: response.data.pagination.currentPage,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.totalSessions
      });
    } catch (error) {
      message.error('加载会话列表失败');
      console.error('加载会话列表失败:', error);
    }
    setLoading(false);
  }, [searchText, accountTypeFilter, isActiveFilter]);

  // 加载踢出日志
  const loadKickoutLogs = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: searchText,
        accountType: accountTypeFilter,
        kickType: kickTypeFilter
      };

      const response = await API.get('/api/admin/kickout-logs', { params });
      setKickoutLogs(response.data.logs);
      setLogPagination({
        current: response.data.pagination.currentPage,
        pageSize: response.data.pagination.limit,
        total: response.data.pagination.totalLogs
      });
    } catch (error) {
      message.error('加载踢出日志失败');
      console.error('加载踢出日志失败:', error);
    }
    setLoading(false);
  }, [searchText, accountTypeFilter, kickTypeFilter]);

  // 初始化数据
  useEffect(() => {
    loadStats();
    if (activeTab === 'sessions') {
      loadSessions();
    } else if (activeTab === 'logs') {
      loadKickoutLogs();
    }
  }, [activeTab, loadStats, loadSessions, loadKickoutLogs]);

  // 搜索处理
  const handleSearch = () => {
    if (activeTab === 'sessions') {
      loadSessions(1, pagination.pageSize);
    } else if (activeTab === 'logs') {
      loadKickoutLogs(1, logPagination.pageSize);
    }
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    setAccountTypeFilter(undefined);
    setIsActiveFilter(undefined);
    setKickTypeFilter(undefined);
    if (activeTab === 'sessions') {
      loadSessions();
    } else if (activeTab === 'logs') {
      loadKickoutLogs();
    }
  };

  // 踢出单个会话
  const handleKickSession = async (sessionId, reason = '管理员强制下线') => {
    try {
      await API.delete(`/api/admin/sessions/${sessionId}`, {
        data: { reason }
      });
      message.success('会话已成功踢出');
      loadSessions(pagination.current, pagination.pageSize);
      loadStats();
    } catch (error) {
      message.error('踢出会话失败');
      console.error('踢出会话失败:', error);
    }
  };

  // 踢出用户所有会话
  const handleKickUserAllSessions = async (userId, username, reason = '管理员强制下线所有会话') => {
    try {
      await API.delete(`/api/admin/users/${userId}/sessions`, {
        data: { reason }
      });
      message.success(`已踢出用户 ${username} 的所有会话`);
      loadSessions(pagination.current, pagination.pageSize);
      loadStats();
    } catch (error) {
      message.error('踢出用户会话失败');
      console.error('踢出用户会话失败:', error);
    }
  };

  // 清理过期会话
  const handleCleanupExpiredSessions = () => {
    confirm({
      title: '确认清理过期会话',
      content: '此操作将清理所有已过期的会话记录，是否继续？',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          const response = await API.post('/api/admin/sessions/cleanup');
          const result = response.data;
          
          // 显示详细的清理结果
          Modal.success({
            title: '清理完成',
            width: 600,
            content: (
              <div>
                <Alert
                  message={result.message || `成功清理了 ${result.deletedCount || result.cleanedCount || 0} 个过期会话`}
                  type="success"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
                
                {result.beforeCleanup && result.afterCleanup && (
                  <div>
                    <h4 style={{ marginBottom: '12px' }}>清理统计</h4>
                    
                    <Row gutter={16} style={{ marginBottom: '16px' }}>
                      <Col span={12}>
                        <Card size="small" title="清理前">
                          <div style={{ fontSize: '12px' }}>
                            <div>总会话数: <strong>{result.beforeCleanup.totalSessions}</strong></div>
                            <div>活跃会话: <strong>{result.beforeCleanup.activeSessions}</strong></div>
                            <div>失效会话: <strong>{result.beforeCleanup.inactiveSessions}</strong></div>
                            <div>过期会话: <strong>{result.beforeCleanup.allExpiredSessions}</strong></div>
                          </div>
                        </Card>
                      </Col>
                      <Col span={12}>
                        <Card size="small" title="清理后">
                          <div style={{ fontSize: '12px' }}>
                            <div>总会话数: <strong>{result.afterCleanup.totalSessions}</strong></div>
                            <div>活跃会话: <strong>{result.afterCleanup.activeSessions}</strong></div>
                            <div>失效会话: <strong>{result.afterCleanup.inactiveSessions}</strong></div>
                          </div>
                        </Card>
                      </Col>
                    </Row>
                    
                    {result.details && (
                      <Alert
                        message="清理详情"
                        description={
                          <div style={{ fontSize: '12px' }}>
                            <div>删除过期会话: <strong style={{ color: '#f5222d' }}>{result.details.expiredSessionsDeleted}</strong> 个</div>
                            <div>总会话变化: <strong>{result.details.totalSessionsChange}</strong></div>
                            <div>失效会话变化: <strong>{result.details.inactiveSessionsChange}</strong></div>
                          </div>
                        }
                        type="info"
                        showIcon
                      />
                    )}
                  </div>
                )}
              </div>
            ),
            onOk: () => {
              loadSessions(pagination.current, pagination.pageSize);
              loadStats();
            }
          });
        } catch (error) {
          message.error('清理过期会话失败');
          console.error('清理过期会话失败:', error);
        }
      }
    });
  };

  // 显示会话详情
  const showSessionDetail = (session) => {
    setSelectedSession(session);
    setSessionDetailVisible(true);
  };

  // 显示踢出表单
  const showKickForm = (session) => {
    setSelectedSession(session);
    setKickFormVisible(true);
    kickForm.resetFields();
  };

  // 处理踢出表单提交
  const handleKickFormSubmit = async (values) => {
    if (selectedSession) {
      await handleKickSession(selectedSession.sessionId, values.reason);
      setKickFormVisible(false);
      setSelectedSession(null);
    }
  };

  // 格式化设备信息
  const formatDeviceInfo = (deviceInfo) => {
    if (!deviceInfo) return '未知设备';
    
    if (typeof deviceInfo === 'string') {
      try {
        deviceInfo = JSON.parse(deviceInfo);
      } catch {
        return deviceInfo;
      }
    }

    return deviceInfo.displayName || 
           `${deviceInfo.browser || '未知浏览器'} on ${deviceInfo.os || '未知系统'}`;
  };

  // 会话列表列定义
  const sessionColumns = [
    {
      title: '用户信息',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.user?.username || record.username}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Tag color={record.accountType === 'main' ? 'blue' : 'orange'}>
              {record.accountType === 'main' ? '主账号' : 'RAM账号'}
            </Tag>
            {record.user?.role && (
              <Tag color={record.user.role === 'admin' ? 'red' : 'green'}>
                {record.user.role === 'admin' ? '管理员' : '用户'}
              </Tag>
            )}
          </div>
        </div>
      )
    },
    {
      title: '设备信息',
      key: 'device',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {record.deviceDescription || formatDeviceInfo(record.deviceInfo)}
          </div>
          {record.deviceType && (
            <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
              <Tag size="small" color="blue">{record.deviceType}</Tag>
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#999' }}>
            IP: {record.ipAddress}
          </div>
          {record.location && (
            <div style={{ fontSize: '11px', color: '#999' }}>
              <GlobalOutlined /> {record.location}
            </div>
          )}
        </div>
      )
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <div>
          <div>
            <Badge 
              status={record.isActive && !record.isExpired ? 'success' : 'error'} 
              text={record.isActive && !record.isExpired ? '活跃' : '失效'}
            />
          </div>
          <div style={{ marginTop: 4 }}>
            <Badge 
              status={record.isOnline ? 'processing' : 'default'} 
              text={record.isOnline ? '在线' : '离线'}
            />
          </div>
        </div>
      )
    },
    {
      title: '时间信息',
      key: 'time',
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>
            <ClockCircleOutlined /> 登录时间
          </div>
          <div style={{ color: '#666' }}>
            {new Date(record.loginTime).toLocaleString()}
          </div>
          <div style={{ marginTop: 4 }}>
            <WifiOutlined /> 最后活动
          </div>
          <div style={{ color: '#666' }}>
            {formatDistanceToNow(new Date(record.lastActivity), { 
              addSuffix: true, 
              locale: zhCN 
            })}
          </div>
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button 
            size="small" 
            onClick={() => showSessionDetail(record)}
          >
            详情
          </Button>
          {record.isActive && !record.isExpired && (
            <>
              <Button 
                size="small" 
                danger 
                onClick={() => showKickForm(record)}
              >
                踢出
              </Button>
              {record.user?._id && (
                <Popconfirm
                  title="确认踢出该用户的所有会话？"
                  onConfirm={() => handleKickUserAllSessions(
                    record.user._id, 
                    record.user.username || record.username
                  )}
                >
                  <Button size="small" danger type="link">
                    踢出全部
                  </Button>
                </Popconfirm>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  // 踢出日志列定义
  const logColumns = [
    {
      title: '被踢出用户',
      key: 'kickedUser',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.kickedUser?.username || record.kickedUsername}
          </div>
          <Tag color={record.accountType === 'main' ? 'blue' : 'orange'}>
            {record.accountType === 'main' ? '主账号' : 'RAM账号'}
          </Tag>
        </div>
      )
    },
    {
      title: '踢出类型',
      key: 'kickType',
      width: 100,
      render: (_, record) => {
        const getKickTypeDisplay = (kickType) => {
          switch (kickType) {
            case 'admin':
              return { text: '管理员踢出', color: 'red' };
            case 'login':
              return { text: '登录踢出', color: 'purple' };
            case 'logout':
              return { text: '主动退出', color: 'green' };
            default:
              return { text: '自动踢出', color: 'orange' };
          }
        };

        const displayInfo = getKickTypeDisplay(record.kickType);
        
        return (
          <div>
            <Tag color={displayInfo.color}>
              {displayInfo.text}
            </Tag>
            {record.kickType === 'admin' && record.adminUser && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                操作员: {record.adminUser.username}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: '踢出原因',
      dataIndex: 'kickReason',
      key: 'kickReason',
      width: 150,
      ellipsis: true
    },
    {
      title: 'IP地址变化',
      key: 'ipChange',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>原IP: {record.kickedIp || '未知'}</div>
          <div>新IP: {record.newLoginIp || '未知'}</div>
        </div>
      )
    },
    {
      title: '位置变化',
      key: 'locationChange',
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>原位置: {record.kickedLocation || '未知'}</div>
          <div>新位置: {record.newLoginLocation || '未知'}</div>
        </div>
      )
    },
    {
      title: '踢出时间',
      key: 'kickTime',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          {new Date(record.kickTime).toLocaleString()}
        </div>
      )
    }
  ];

  // Tab项配置
  const tabItems = [
    {
      key: 'sessions',
      label: '会话列表',
      children: (
        <div>
          {/* 搜索和筛选 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Search
                  placeholder="搜索用户名、IP地址或位置"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                />
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="账号类型"
                  value={accountTypeFilter}
                  onChange={setAccountTypeFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="main">主账号</Option>
                  <Option value="ram">RAM账号</Option>
                </Select>
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="活跃状态"
                  value={isActiveFilter}
                  onChange={setIsActiveFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="true">活跃</Option>
                  <Option value="false">失效</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Space>
                  <Button onClick={handleReset}>重置</Button>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={() => loadSessions()}
                  >
                    刷新
                  </Button>
                  <Button 
                    danger 
                    icon={<ClearOutlined />} 
                    onClick={handleCleanupExpiredSessions}
                  >
                    清理过期会话
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* 会话表格 */}
          <Table
            columns={sessionColumns}
            dataSource={sessions}
            rowKey="sessionId"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                loadSessions(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                loadSessions(1, size);
              }
            }}
            scroll={{ x: 1000 }}
          />
        </div>
      )
    },
    {
      key: 'logs',
      label: '踢出日志',
      children: (
        <div>
          {/* 搜索栏 */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Search
                  placeholder="搜索用户名、IP地址或管理员"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  enterButton={<SearchOutlined />}
                />
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="账号类型"
                  value={accountTypeFilter}
                  onChange={setAccountTypeFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="main">主账号</Option>
                  <Option value="ram">RAM账号</Option>
                </Select>
              </Col>
              <Col xs={12} sm={4}>
                <Select
                  placeholder="踢出类型"
                  value={kickTypeFilter}
                  onChange={setKickTypeFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="admin">管理员踢出</Option>
                  <Option value="login">登录踢出</Option>
                  <Option value="logout">主动退出</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Space>
                  <Button onClick={handleReset}>重置</Button>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={() => loadKickoutLogs()}
                  >
                    刷新
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* 踢出日志表格 */}
          <Table
            columns={logColumns}
            dataSource={kickoutLogs}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...logPagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              onChange: (page, pageSize) => {
                loadKickoutLogs(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                loadKickoutLogs(1, size);
              }
            }}
            scroll={{ x: 1000 }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>
        <UserOutlined /> 会话管理
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="活跃会话"
              value={stats.validSessions || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="在线用户"
              value={stats.onlineSessionsCount || 0}
              prefix={<WifiOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="今日登录"
              value={stats.todayLogins || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="今日踢出"
              value={stats.todayKickouts || 0}
              prefix={<DisconnectOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 踢出类型统计 */}
      {(stats.todayAdminKickouts || stats.todayLoginKickouts || stats.todayUserLogouts) && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="管理员踢出"
                value={stats.todayAdminKickouts || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#fa541c' }}
                suffix="次"
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                今日管理员手动踢出
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="登录踢出"
                value={stats.todayLoginKickouts || 0}
                prefix={<WifiOutlined />}
                valueStyle={{ color: '#722ed1' }}
                suffix="次"
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                今日新登录自动踢出
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="主动退出"
                value={stats.todayUserLogouts || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
                suffix="次"
              />
              <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                今日用户主动退出
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 分布统计 */}
      {((stats.mainAccountSessions || stats.ramAccountSessions) || 
        (stats.deviceStats && Object.keys(stats.deviceStats).length > 0)) && (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>分布统计</Title>
          
          <Row gutter={24}>
            {/* 账号类型分布 */}
            {(stats.mainAccountSessions || stats.ramAccountSessions) && (
              <Col xs={24} lg={8}>
                <div>
                  <Title level={6} style={{ marginBottom: 16 }}>账号类型分布</Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          type="circle"
                          size={80}
                          percent={Math.round((stats.mainAccountSessions / (stats.mainAccountSessions + stats.ramAccountSessions)) * 100)}
                          format={() => stats.mainAccountSessions}
                        />
                        <div style={{ marginTop: 8 }}>主账号</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          type="circle"
                          size={80}
                          percent={Math.round((stats.ramAccountSessions / (stats.mainAccountSessions + stats.ramAccountSessions)) * 100)}
                          format={() => stats.ramAccountSessions}
                        />
                        <div style={{ marginTop: 8 }}>RAM账号</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}

            {/* 设备类型分布 */}
            {stats.deviceStats && Object.keys(stats.deviceStats).length > 0 && (
              <Col xs={24} lg={(stats.mainAccountSessions || stats.ramAccountSessions) ? 16 : 24}>
                <div>
                  <Title level={6} style={{ marginBottom: 16 }}>设备类型分布</Title>
                  <Row gutter={[16, 16]}>
                    {Object.entries(stats.deviceStats).map(([deviceType, count]) => {
                      const total = Object.values(stats.deviceStats).reduce((sum, val) => sum + val, 0);
                      const percent = Math.round((count / total) * 100);
                      
                      // 设备类型图标和颜色映射
                      const deviceTypeConfig = {
                        'Desktop': { icon: '🖥️', color: '#1890ff' },
                        'Mobile': { icon: '📱', color: '#52c41a' },
                        'Tablet': { icon: '📱', color: '#faad14' },
                        'TV': { icon: '📺', color: '#722ed1' },
                        'Console': { icon: '🎮', color: '#eb2f96' },
                        'Wearable': { icon: '⌚', color: '#13c2c2' },
                        'Unknown': { icon: '❓', color: '#999' }
                      };
                      
                      const config = deviceTypeConfig[deviceType] || deviceTypeConfig['Unknown'];
                      
                      return (
                        <Col xs={12} sm={8} md={6} lg={6} xl={4} key={deviceType}>
                          <div style={{ textAlign: 'center' }}>
                            <Progress
                              type="circle"
                              size={60}
                              percent={percent}
                              format={() => `${config.icon}`}
                              strokeColor={config.color}
                            />
                            <div style={{ marginTop: 8, fontSize: '12px' }}>
                              <div style={{ fontWeight: 'bold' }}>{deviceType}</div>
                              <div style={{ color: '#666' }}>{count} 个会话</div>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* 主要内容标签页 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* 会话详情弹窗 */}
      <Modal
        title="会话详情"
        open={sessionDetailVisible}
        onCancel={() => setSessionDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSessionDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedSession && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="会话ID" span={2}>
              <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>
                {selectedSession.sessionId}
              </code>
            </Descriptions.Item>
            <Descriptions.Item label="用户名">
              {selectedSession.user?.username || selectedSession.username}
            </Descriptions.Item>
            <Descriptions.Item label="账号类型">
              <Tag color={selectedSession.accountType === 'main' ? 'blue' : 'orange'}>
                {selectedSession.accountType === 'main' ? '主账号' : 'RAM账号'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="用户角色">
              {selectedSession.user?.role && (
                <Tag color={selectedSession.user.role === 'admin' ? 'red' : 'green'}>
                  {selectedSession.user.role === 'admin' ? '管理员' : '用户'}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="用户邮箱">
              {selectedSession.user?.email || '未知'}
            </Descriptions.Item>
            <Descriptions.Item label="IP地址">
              <code>{selectedSession.ipAddress}</code>
            </Descriptions.Item>
            <Descriptions.Item label="登录位置">
              <span>
                <GlobalOutlined style={{ marginRight: '4px' }} />
                {selectedSession.location || '未知'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="设备信息" span={2}>
              <div style={{ fontSize: '13px' }}>
                {formatDeviceInfo(selectedSession.deviceInfo)}
                {selectedSession.deviceInfo && typeof selectedSession.deviceInfo === 'object' && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    {/* 浏览器信息 */}
                    <div>浏览器: {
                      selectedSession.deviceInfo.browserInfo ? 
                      `${selectedSession.deviceInfo.browserInfo.name} ${selectedSession.deviceInfo.browserInfo.version}` :
                      (selectedSession.deviceInfo.browser || '未知')
                    }</div>
                    
                    {/* 操作系统信息 */}
                    <div>操作系统: {
                      selectedSession.deviceInfo.osInfo ? 
                      `${selectedSession.deviceInfo.osInfo.name} ${selectedSession.deviceInfo.osInfo.version}` :
                      (selectedSession.deviceInfo.os || '未知')
                    }</div>
                    
                    {/* 设备类型 */}
                    <div>设备类型: {
                      selectedSession.deviceType || 
                      selectedSession.deviceInfo?.deviceType || 
                      (selectedSession.deviceInfo?.isMobile ? '移动设备' : '桌面设备')
                    }</div>
                    
                    {/* 屏幕分辨率 */}
                    <div>屏幕分辨率: {
                      selectedSession.deviceInfo.screenInfo ? 
                      `${selectedSession.deviceInfo.screenInfo.physicalWidth || selectedSession.deviceInfo.screenInfo.width}x${selectedSession.deviceInfo.screenInfo.physicalHeight || selectedSession.deviceInfo.screenInfo.height}` :
                      (selectedSession.deviceInfo.screen || '未知')
                    }</div>
                    
                    {/* 详细屏幕信息 */}
                    {selectedSession.deviceInfo.screenInfo && (
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                        {selectedSession.deviceInfo.screenInfo.physicalWidth && (
                          <div>物理分辨率: {selectedSession.deviceInfo.screenInfo.physicalWidth}x{selectedSession.deviceInfo.screenInfo.physicalHeight}</div>
                        )}
                        <div>逻辑分辨率: {selectedSession.deviceInfo.screenInfo.width}x{selectedSession.deviceInfo.screenInfo.height}</div>
                        {selectedSession.deviceInfo.screenInfo.devicePixelRatio && selectedSession.deviceInfo.screenInfo.devicePixelRatio > 1 && (
                          <div>像素比: {selectedSession.deviceInfo.screenInfo.devicePixelRatio}x</div>
                        )}
                        <div>色彩深度: {selectedSession.deviceInfo.screenInfo.colorDepth}位</div>
                      </div>
                    )}
                    
                    {/* 可用屏幕区域 */}
                    {selectedSession.deviceInfo.screenInfo && (
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                        {selectedSession.deviceInfo.screenInfo.physicalAvailWidth ? (
                          <div>可用区域: {selectedSession.deviceInfo.screenInfo.physicalAvailWidth}x{selectedSession.deviceInfo.screenInfo.physicalAvailHeight} (物理)</div>
                        ) : (
                          <div>可用区域: {selectedSession.deviceInfo.screenInfo.availWidth}x{selectedSession.deviceInfo.screenInfo.availHeight} (逻辑)</div>
                        )}
                      </div>
                    )}
                    
                    {/* 时区和语言 */}
                    <div>时区: {selectedSession.deviceInfo.timezone || '未知'}</div>
                    <div>语言: {selectedSession.deviceInfo.language || '未知'}</div>
                    
                    {/* 平台信息 */}
                    {selectedSession.deviceInfo.platform && (
                      <div>平台: {selectedSession.deviceInfo.platform}</div>
                    )}
                    
                    {/* Cookie和在线状态 */}
                    {selectedSession.deviceInfo.cookieEnabled !== undefined && (
                      <div>Cookie支持: {selectedSession.deviceInfo.cookieEnabled ? '是' : '否'}</div>
                    )}
                    
                    {selectedSession.deviceInfo.onLine !== undefined && (
                      <div>登录时在线状态: {selectedSession.deviceInfo.onLine ? '在线' : '离线'}</div>
                    )}
                    
                    {/* 时间戳 */}
                    {selectedSession.deviceInfo.timestamp && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
                        设备信息收集时间: {new Date(selectedSession.deviceInfo.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="登录时间">
              <div>
                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                {new Date(selectedSession.loginTime).toLocaleString()}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="最后活动">
              <div>
                <WifiOutlined style={{ marginRight: '4px' }} />
                {new Date(selectedSession.lastActivity).toLocaleString()}
                <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                  ({formatDistanceToNow(new Date(selectedSession.lastActivity), { 
                    addSuffix: true, 
                    locale: zhCN 
                  })})
                </div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="过期时间">
              <div>
                {new Date(selectedSession.expiresAt).toLocaleString()}
                <div style={{ fontSize: '11px', color: selectedSession.isExpired ? '#f5222d' : '#52c41a', marginTop: '2px' }}>
                  {selectedSession.isExpired ? '已过期' : '有效'}
                </div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="当前状态">
              <Space orientation="vertical" size="small">
                <div>
                  <Badge 
                    status={selectedSession.isActive && !selectedSession.isExpired ? 'success' : 'error'} 
                    text={selectedSession.isActive && !selectedSession.isExpired ? '会话活跃' : '会话失效'}
                  />
                </div>
                <div>
                  <Badge 
                    status={selectedSession.isOnline ? 'processing' : 'default'} 
                    text={selectedSession.isOnline ? 'WebSocket在线' : 'WebSocket离线'}
                  />
                </div>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* 踢出确认弹窗 */}
      <Modal
        title="踢出会话"
        open={kickFormVisible}
        onCancel={() => setKickFormVisible(false)}
        footer={null}
      >
        <Alert
          message="确认踢出会话"
          description={`即将踢出用户 ${selectedSession?.user?.username || selectedSession?.username} 的会话，此操作不可撤销。`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Form
          form={kickForm}
          onFinish={handleKickFormSubmit}
          layout="vertical"
        >
          <Form.Item
            label="踢出原因"
            name="reason"
            initialValue="管理员强制下线"
            rules={[{ required: true, message: '请输入踢出原因' }]}
          >
            <Input.TextArea
              placeholder="请输入踢出原因"
              rows={3}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setKickFormVisible(false)}>
                取消
              </Button>
              <Button type="primary" danger htmlType="submit">
                确认踢出
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SessionManagement; 